import type { MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';

type Activities = { [templateId: string]: MaintenanceActivity };
type RemovedActivities = { [templateId: string]: true };

interface ActivitySide {
    /** IDs of the templates the component gets from its own type (always shown on it) */
    typeTemplateIds: string[];
    /** The component's stored `maintenanceActivities` in the draft */
    activities: Activities | undefined;
    /** The component's `removedActivities` in the draft: type rows removed from it */
    removed?: RemovedActivities;
}

export interface ActivityCopyPlan {
    /** Source activities the target does not show today */
    added: string[];
    /** Source activities the target already shows; their settings are replaced */
    replaced: string[];
    /** Rows shown on the target that the source lacks; they are removed from it */
    removed: string[];
    /** The target's new `maintenanceActivities` */
    activities: Activities;
    /** The target's new `removedActivities`; undefined when nothing is removed */
    removedActivities: RemovedActivities | undefined;
}

/**
 * The activities shown on a component: its type's templates plus the cross-type
 * ones stored on it. Stored entries whose template no longer exists are skipped,
 * and so are the rows removed from the component.
 */
export const shownActivityIds = (side: ActivitySide, templateExists: (id: string) => boolean): string[] => {
    const ids = new Set(side.typeTemplateIds);
    Object.keys(side.activities ?? {}).forEach(id => {
        if (templateExists(id)) ids.add(id);
    });
    return Array.from(ids).filter(id => side.removed?.[id] !== true);
};

/**
 * Plans "Kopiera alla aktiviteter från annan komponent": the target's activities
 * become exactly the source's.
 *
 * Every draft row stores a date and an interval, so "what was set on the
 * source" is what deviates from the source's own default (`sourceDefault`).
 * A copied row starts from the target's default (`targetDefault`: its own
 * condition and direction) and takes the source's date and interval only
 * where they deviate; cost settings, investment share and the row's comment
 * are taken as stored.
 * Custom quantity and completion tracking describe the component itself and
 * are never copied; the target's own custom quantity is cleared with the rest.
 *
 * A row of the target's own type that the source lacks is removed with a
 * marker, like a row removed by hand, so it is not re-created.
 */
export const planActivityCopy = (
    source: ActivitySide,
    target: ActivitySide,
    templateExists: (id: string) => boolean,
    sourceDefault: (templateId: string) => MaintenanceActivity,
    targetDefault: (templateId: string) => MaintenanceActivity
): ActivityCopyPlan => {
    const sourceIds = shownActivityIds(source, templateExists);
    const sourceIdSet = new Set(sourceIds);
    const targetShown = new Set(shownActivityIds(target, templateExists));

    const activities: Activities = {};

    sourceIds.forEach(id => {
        const stored = source.activities?.[id];
        const activity = targetDefault(id);
        if (stored) {
            const standard = sourceDefault(id);
            if (stored.nextDate && stored.nextDate !== standard.nextDate) {
                activity.nextDate = stored.nextDate;
            }
            if (stored.intervalValue && stored.intervalType
                && (stored.intervalValue !== standard.intervalValue || stored.intervalType !== standard.intervalType)) {
                activity.intervalValue = stored.intervalValue;
                activity.intervalType = stored.intervalType;
            }
            // Only defined values: Firebase rejects undefined
            if (stored.cost !== undefined) activity.cost = stored.cost;
            if (stored.costSource !== undefined) activity.costSource = stored.costSource;
            if (stored.customUnitPrice !== undefined) activity.customUnitPrice = stored.customUnitPrice;
            if (stored.costComment !== undefined) activity.costComment = stored.costComment;
            if (stored.costAttachments !== undefined) activity.costAttachments = stored.costAttachments;
            if (stored.investmentPercentage !== undefined) activity.investmentPercentage = stored.investmentPercentage;
            if (stored.comment) activity.comment = stored.comment;
        }
        activities[id] = activity;
    });

    const removedActivities: RemovedActivities = {};
    target.typeTemplateIds.forEach(id => {
        if (!sourceIdSet.has(id)) removedActivities[id] = true;
    });

    return {
        added: sourceIds.filter(id => !targetShown.has(id)),
        replaced: sourceIds.filter(id => targetShown.has(id)),
        removed: Array.from(targetShown).filter(id => !sourceIdSet.has(id)),
        activities,
        removedActivities: Object.keys(removedActivities).length > 0 ? removedActivities : undefined
    };
};
